import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  SchemeType,
  SettingCustomer,
} from '../../../../../interfaces/merchants/products/purchases';
import { CustomerFieldsValueType } from '../../../../../interfaces/merchants/products/purchases/inputs';
import {
  clearCustomerError,
  inputPurchasingUserInfo,
} from '../../../../../redux/actions/merchants/products/purchases/inputs';
import * as $ from '../../../../../helpers/merchants/products/purchases/helper';
import CustomerFields, {
  createInitialState as customerFieldsInitialState,
  submittable as customerFieldsSubmittable,
} from './components/CustomerField';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import { getInputs } from '../../../../../redux/selectors/merchants/products/purchases/inputs';
import { useAppDispatch } from '../../../../../redux';
import { RSButton } from 'src/reservation-page/RSButton';

interface ElementProps {
  schemeType: SchemeType;
  settings: SettingCustomer;
  postable: boolean;
  onSubmit: () => void;
}

enum FieldNames {
  CustomerFields = 'customerFields',
}

export type InputValueType = string | boolean;

interface FieldValues {
  customerFields: CustomerFieldsValueType;
}

type Props = ElementProps;

const UserInfo: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { settings, postable } = props;
  const intl = useIntl();

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getInputs(state.merchantsProductsPurchasesInputs),
    }));
  const { inputs } = useSelector();

  useEffect(() => {
    scrollToTop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [fieldValues, setFieldValues] = useState<FieldValues>({
    customerFields:
      !!inputs && inputs.userInfo
        ? inputs.userInfo
        : customerFieldsInitialState(settings.customerFields),
  });
  const submittable = useMemo(() => {
    return customerFieldsSubmittable(
      settings.customerFields!,
      $.getValueByNestedString(fieldValues, FieldNames.CustomerFields)!,
    );
  }, [fieldValues, settings.customerFields]);

  const handleChange = useCallback(
    (name: string, value: InputValueType) => {
      const changedState = $.changeObjectByNestedString(
        fieldValues,
        name,
        value,
      );
      setFieldValues(changedState as FieldValues);
      dispatch(clearCustomerError(name));
    },
    [dispatch, fieldValues],
  );

  const handleSubmit = useCallback(async () => {
    const { schemeType, onSubmit } = props;
    try {
      await dispatch(
        inputPurchasingUserInfo(schemeType, inputs.productId!, fieldValues),
      );
      onSubmit();
    } catch (e) {
      // failed
    }
  }, [dispatch, props, inputs, fieldValues]);

  return (
    <>
      {settings.customerFields && (
        <CustomerFields
          customerFields={settings.customerFields}
          onChange={handleChange}
          namePrefix={FieldNames.CustomerFields}
          values={
            ($.getValueByNestedString(
              fieldValues,
              FieldNames.CustomerFields,
            ) as Record<string, string>) || {}
          }
        />
      )}
      <div className="px-[18px] py-5">
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={!submittable || !postable}
          value={intl.formatMessage({ id: 'purchases.userInfo.submitButton' })}
          onClick={handleSubmit}
        >
          {intl.formatMessage({ id: 'purchases.userInfo.submitButton' })}
        </RSButton>
      </div>
    </>
  );
};

export default UserInfo;
